// Imports necesarios...
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { SolicitarVisitaService, PagedRequestOptions } from 'src/app/services/solicitar-visita.service'; // <-- Importar la nueva interfaz
import { ClientesService } from 'src/app/services/clientes.service'; //  CR-202608-0010
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { StorageService } from 'src/app/services/storage.service';
import { Subscription, Subject, merge, of as observableOf } from 'rxjs';
import { debounceTime, distinctUntilChanged, catchError } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { PaginatorIntl } from 'src/app/paginator-intl';
import { SharedModule } from '@app/shared/shared.module';
import { TipoServicioService } from '@app/services/tipo-servicio.service';

@Component({
  selector: 'app-solicitudes-de-visita',
  standalone: true,
  imports: [
    CommonModule, MatCardModule, MatTableModule, MatIconModule, MatButtonModule,
    MatPaginatorModule, MatSortModule, MatFormFieldModule, MatInputModule,
    RouterModule, MatProgressSpinnerModule, MatTooltipModule,
    MatSelectModule, SharedModule
  ],
  templateUrl: './solicitudes-de-visita.component.html',
    styleUrls: ['./solicitudes-de-visita.component.scss'],
  providers: [
    { provide: MatPaginatorIntl, useClass: PaginatorIntl }
  ],
})
export class SolicitudesDeVisitaComponent implements OnInit, AfterViewInit, OnDestroy {
  dataSource = new MatTableDataSource<any>();
  
  // Define las columnas base que ven todos los usuarios.
  baseDisplayedColumns: string[] = [
    'id', 
    'fechaIngreso', 
    'local', 
    'direccion',
    'comuna',
    'zona',
    'especialidad',
    'ticketGruman',
    'observaciones',
    'generado_por', 
    'tipoServicio', 
    'tipo_solicitud', 
    'tiempo_restante', 
    'estado',
    'imagenes',
    'acciones'
  ];
  
  // Este array se construirá dinámicamente
  displayedColumns: string[] = [...this.baseDisplayedColumns];

  selectedCompany: any = null;
  private companySubscription: Subscription | null = null;

  public isGruman: boolean = false;
  public clientes: any[] = [];
  public selectedClientId: number | null = null;
  
  loading = true;
  totalItems: number = 0;
  selectedServiceType: string = '';
  tiposServicio: any[] = [];

  private searchSubject = new Subject<string>();

  meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  anios: number[] = [];
  selectedMes: string = '';
  selectedAnio: string = '';

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('input') input!: ElementRef;

  constructor(
    private solicitarVisitaService: SolicitarVisitaService, 
    private tipoServicioService: TipoServicioService,
    private clientesService: ClientesService,
    private router: Router,
    private storage: StorageService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.generarAnios();
    this.getTiposServicio();

    // Capturar si venimos desde el Dashboard con un mes específico
    this.route.queryParams.subscribe((params: any) => {
      if (params['mesFacturacion']) {
        const parts = params['mesFacturacion'].split(' ');
        if (parts.length === 2) {
          this.selectedMes = parts[0];
          this.selectedAnio = parts[1];
        }
      }
    });

    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.paginator.pageIndex = 0;
      this.loadSolicitudes();
    });
  

    this.companySubscription = this.storage.companyChange$.subscribe((company: any) => {      
      this.selectedCompany = company;
      
      // Actualizar estado de isGruman y cargar clientes si aplica
      this.isGruman = this.selectedCompany?.nombre?.trim().toUpperCase() === 'GRUMAN';
      this.selectedClientId = null;
      if (this.isGruman) {
        this.loadClientes();
      }

      this.updateDisplayedColumns(this.selectedCompany);

      if (this.paginator) {
        this.paginator.pageIndex = 0;
        this.loadSolicitudes();
      }
    });

    const user = this.storage.getItem('currentUser');
    if (user && user.selectedCompany) {      
      this.selectedCompany = user.selectedCompany;
      // Inicializar el filtro de cliente en la carga si es GRUMAN
      this.isGruman = this.selectedCompany.nombre?.trim().toUpperCase() === 'GRUMAN';
      this.updateDisplayedColumns(this.selectedCompany);
      if (this.isGruman) {
        this.loadClientes();
      }
    } else {      
      this.updateDisplayedColumns(null);
    }
  }

  // Cargar catálogo de clientes para el dropdown
  loadClientes(): void {
    this.clientesService.getClientesWithGruman().subscribe({
      next: (clientes: any[]) => this.clientes = clientes || [],
      error: (err: any) => console.error('Error al cargar clientes:', err)
    });
  }

  // Evento al seleccionar un cliente en la vista GRUMAN
  onClientFilterChange(): void {
    if (this.paginator) this.paginator.pageIndex = 0;
    this.loadSolicitudes();
  }

  ngAfterViewInit() {
    // Escuchar cambios en el ordenamiento y la paginación para recargar los datos
    merge(this.sort.sortChange, this.paginator.page).subscribe(() => {
      this.loadSolicitudes();
    });
    
    // Carga inicial
    this.loadSolicitudes();
  }

  ngOnDestroy() {
    if (this.companySubscription) {
      this.companySubscription.unsubscribe();
    }
  }

  generarAnios(): void {
    const currentYear = new Date().getFullYear();
    for (let y = 2023; y <= currentYear + 2; y++) {
      this.anios.push(y);
    }
  }

  onDateFilterChange() {
    if (this.paginator) this.paginator.pageIndex = 0;
    this.loadSolicitudes();
  }

  getTiposServicio(): void {
    this.tipoServicioService.findAll().subscribe({
      next: (data: any) => {
        this.tiposServicio = data;
      },
      error: (err: any) => console.error('Error cargando tipos de servicio', err)
    });
  }

  /** 
   * Actualiza las columnas visibles basándose en el perfil de la compañía.   
   */
  private updateDisplayedColumns(company: any) {
    
    // Comienza con las columnas base (que ahora incluyen 'acciones')
    const newCols = [...this.baseDisplayedColumns];
    let esGruman = false;    
    
    if (company && company.nombre) {      
      const nombreLimpio = company.nombre.trim().toUpperCase();
      
      if (nombreLimpio === 'GRUMAN') {
        esGruman = true;
      }
    }

    // Añade la columna 'cliente' en la posición 2 SOLO si el usuario es de Gruman
    if (esGruman) {
      newCols.splice(2, 0, 'cliente');      
    }

    // Asigna el nuevo array de columnas
    this.displayedColumns = newCols;
  }
  
  loadSolicitudes() {
    this.loading = true;

    let mesFacturacionStr = '';
    if (this.selectedMes) mesFacturacionStr += this.selectedMes;
    if (this.selectedAnio) mesFacturacionStr += (mesFacturacionStr ? ' ' : '') + this.selectedAnio;
    
    // 1. Construir el objeto de opciones
    const options: PagedRequestOptions = {
      page: this.paginator.pageIndex + 1,
      limit: this.paginator.pageSize,
      searchTerm: this.input.nativeElement.value,
      serviceType: this.selectedServiceType,
      sortBy: this.sort.active || 'id',
      sortOrder: this.sort.direction || 'desc',
      mesFacturacion: mesFacturacionStr || undefined
    };

    // Añadir clientId solo si es necesario
    if (this.selectedCompany && !this.isGruman) {
      options.clientId = this.selectedCompany.id;
    } else if (this.isGruman && this.selectedClientId) {
      options.clientId = this.selectedClientId;
    }
    
    // 2. Llamar al servicio con el objeto de opciones
    this.solicitarVisitaService.getPendientesPaginado(options)
    .pipe(
      catchError(() => observableOf(null)) // Manejo de errores
    ).subscribe((response: any) => {
      this.loading = false;
      this.totalItems = response ? response.total : 0;
      this.dataSource.data = response ? response.data : [];
    });
  }
  
  applyFilter() {
    this.searchSubject.next(this.input.nativeElement.value);
  }

  onServiceTypeChange() {
    this.paginator.pageIndex = 0; // Volvemos a la página 1
    this.loadSolicitudes();       // Recargamos la tabla
  }

  resetFilters() {
    this.input.nativeElement.value = '';
    this.selectedServiceType = '';
    this.selectedClientId = null;
    this.paginator.pageIndex = 0;
    this.sort.active = 'id';
    this.sort.direction = 'desc';
    this.selectedMes = '';
    this.selectedAnio = '';
    this.getTiposServicio();
    this.loadSolicitudes();
  }

  exportarExcel(): void {
    Swal.fire({
      title: 'Generando exportación...',
      text: 'Esto puede tardar unos segundos, por favor espera.',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    let mesFacturacionStr = '';
    if (this.selectedMes) mesFacturacionStr += this.selectedMes;
    if (this.selectedAnio) mesFacturacionStr += (mesFacturacionStr ? ' ' : '') + this.selectedAnio;

    const options: PagedRequestOptions | any = {
      export: true, 
      searchTerm: this.input?.nativeElement?.value || '',
      serviceType: this.selectedServiceType,
      sortBy: this.sort?.active || 'id',
      sortOrder: this.sort?.direction || 'desc',
      mesFacturacion: mesFacturacionStr || undefined,
      status: 'pendiente'
    };

    if (this.isGruman) {
      if (this.selectedClientId) {
        options.clientId = this.selectedClientId;
      }
    } else if (this.selectedCompany?.id) {
      options.clientId = this.selectedCompany.id;
    }

    this.solicitarVisitaService.descargarExcelSolicitudes(options).subscribe({
      next: (response: any) => {
        Swal.close();
        // Delegamos el formateo complejo al servicio
        this.solicitarVisitaService.exportarExcelMaestro(response.data, 'Pendientes');
      },
      error: (err: any) => {
        console.error(err);
        Swal.close();
        Swal.fire('Error', 'No se pudo obtener los datos del servidor.', 'error');
      }
    });
  }

  canViewDetail(row: any): boolean {
    if (row.facturacion && (row.facturacion.is_cerrada === true || row.facturacion.is_cerrada === 1)) {
      return false;
    }
    return true;
  }

  verSolicitud(solicitud: any): void {
    this.router.navigate(['/transacciones/solicitudes-de-visita/ver-solicitud/pendiente', solicitud.id]);
  }

  formatDate(date: string) {
    if (!date) return '';
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    });
  }

  getTiempoRestante(solicitud: any) {
    if (!solicitud?.fechaIngreso || !solicitud?.tipo_solicitud) {
      return '-';
    }

    // 1. Manejo robusto de fechas (Evita el problema de hora local vs UTC)
    // Al agregar 'Z' forzamos a que JS lo trate como UTC si viene sin zona horaria
    const fechaStr = solicitud.fechaIngreso.endsWith('Z') 
      ? solicitud.fechaIngreso 
      : solicitud.fechaIngreso + 'Z';
      
    const fechaIngreso = new Date(fechaStr);
    // Congelar el cálculo de SLA basándose en la fecha de inicio/término para evitar falsos "Vencidos"
    let fechaActual = new Date(); // Hora actual del navegador
    if (solicitud.fecha_hora_inicio_servicio) {
      fechaActual = new Date(solicitud.fecha_hora_inicio_servicio.endsWith('Z') ? solicitud.fecha_hora_inicio_servicio : solicitud.fecha_hora_inicio_servicio + 'Z');
    } else if (['finalizada', 'validada'].includes(solicitud.status?.toLowerCase())) {
      if (solicitud.fecha_hora_fin_servicio) {
        fechaActual = new Date(solicitud.fecha_hora_fin_servicio.endsWith('Z') ? solicitud.fecha_hora_fin_servicio : solicitud.fecha_hora_fin_servicio + 'Z');
      } else if (solicitud.fecha_hora_validacion) {
        fechaActual = new Date(solicitud.fecha_hora_validacion.endsWith('Z') ? solicitud.fecha_hora_validacion : solicitud.fecha_hora_validacion + 'Z');
      }
    } else if (solicitud.status?.toLowerCase() === 'reabierta' && solicitud.fecha_hora_fin_servicio) {
      fechaActual = new Date(solicitud.fecha_hora_fin_servicio.endsWith('Z') ? solicitud.fecha_hora_fin_servicio : solicitud.fecha_hora_fin_servicio + 'Z');
    }

    // 2. CORRECCIÓN LÓGICA SLA: Usamos SOLO las horas totales
    // Tu tabla tiene 'sla_hora' con el total (ej: 24, 48, 720). Usaremos ese.
    const slaHorasTotales = solicitud.tipo_solicitud.sla_hora || 0;

    if (slaHorasTotales === 0) return '-';

    // 3. Calcular Fecha Límite
    const fechaLimite = new Date(fechaIngreso);
    fechaLimite.setHours(fechaLimite.getHours() + slaHorasTotales);

    // 4. Calcular diferencia
    const tiempoRestanteMs = fechaLimite.getTime() - fechaActual.getTime();

    // 5. Si ya venció
    if (tiempoRestanteMs <= 0) {
      return 'Vencido';
    }

    // 6. Formatear salida (Días, Horas, Minutos)
    const dias = Math.floor(tiempoRestanteMs / (1000 * 60 * 60 * 24));
    const horas = Math.floor((tiempoRestanteMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutos = Math.floor((tiempoRestanteMs % (1000 * 60 * 60)) / (1000 * 60));

    if (dias > 0) {
      return `${dias}d ${horas}h ${minutos}m`;
    } else if (horas > 0) {
      return `${horas}h ${minutos}m`;
    } else {
      return `${minutos}m`;
    }
  }

  formatDateUTC(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    
    // Usamos getUTC... para leer la fecha "cruda" sin aplicar la zona horaria de Chile
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = date.getUTCFullYear();
    
    return `${day}-${month}-${year}`; // Retorna 19-02-2026
  }
}

